<template>
	<TheForm :model="request" :rules="rules" ref="formRef" :waiting="waiting" :id="id">
		<ButtonSticky @buttonclick="saveStatement()">
			<template v-slot:icon><saveIcon /></template>
			<template v-slot:text>Speichern </template>
		</ButtonSticky>

		<section>
			<ButtonBack />

			<el-row>
				<el-col :sm="24" class="itemtitle">
					<el-form-item prop="title">
						<InputLite
							:inputValue="request.title"
							:placeholder="'Titel der Stellungnahme'"
							@inputenter="(val) => (request.title = val)"
							@inputblur="(val) => (request.title = val)"
						/>
					</el-form-item>
				</el-col>
			</el-row>

			<TheBox :title="'Stellungnahme Infos'" :hasDataGrid="true">
				<el-row>
					<el-col :sm="12" :xs="24">
						<el-form-item label="Stn.-Gruppe:" prop="group">
							{{ selectedOrCreatedContact.user?.roles.length > 0 ? selectedOrCreatedContact.user?.roles?.map((r) => r.name).join(', ') : '-' }}
						</el-form-item>

						<el-form-item label="Stn. erhalten:" prop="answeredDate">
							<el-date-picker v-model="request.answeredDate" type="date" placeholder="TT.MM.JJJJ" :format="'DD.MM.YYYY'" />
						</el-form-item>
					</el-col>

					<el-col :sm="12" :xs="24">
						<el-form-item label="Stn. angefordert:" prop="createdDate">
							<el-date-picker v-model="request.createdDate" type="date" placeholder="TT.MM.JJJJ" :format="'DD.MM.YYYY'" />
						</el-form-item>
					</el-col>
				</el-row>
			</TheBox>

			<TheBox :title="'Stellungnahme Kontakt'" :hasDataGrid="true">
				<el-row>
					<el-col :xs="24" :sm="12">
						<el-form-item label="Kontakt wählen" prop="contactName">
							<el-select
								v-model="request.contactName"
								filterable
								remote
								clearable
								placeholder="Kontakt wählen"
								remote-show-suffix
								:remote-method="doContactSearch"
								:loading="contactsLoading"
								@change="setContact"
								@clear="clearSelection"
								@focus="focusRemoteSearchSelect"
							>
								<el-option v-for="item in filteredContacts" :key="item.label" :label="item.label" :value="item.value" />
							</el-select>
						</el-form-item>

						<el-form-item label="E-Mail-Adresse:" prop="email">
							<el-input v-model="selectedOrCreatedContact.user.email" :disabled="!selectedOrCreatedContact.isCreated" />
						</el-form-item>

						<el-form-item label="Titel (vor):" prop="titleBefore">
							<el-input v-model="selectedOrCreatedContact.user.titleBefore" :disabled="!selectedOrCreatedContact.isCreated" />
						</el-form-item>

						<el-form-item label="Titel (nach):" prop="titleAfter">
							<el-input v-model="selectedOrCreatedContact.user.titleAfter" :disabled="!selectedOrCreatedContact.isCreated" />
						</el-form-item>

						<el-form-item label="Telefon:" prop="phoneNumber">
							<el-input v-model="selectedOrCreatedContact.user.phoneNumber" :disabled="!selectedOrCreatedContact.isCreated" />
						</el-form-item>
					</el-col>

					<el-col :sm="12" :xs="24">
						<el-form-item label="Anrede:" class="datagrid--input-short" prop="gender">
							<el-select
								v-model="selectedOrCreatedContact.user.gender"
								:placeholder="'Bitte wählen'"
								:disabled="!selectedOrCreatedContact?.isCreated"
							>
								<el-option v-for="item in genderOptions" :key="item.value" :label="item.label" :value="item.value" />
							</el-select>
						</el-form-item>

						<el-form-item label="Firma:" prop="company">
							<el-input v-model="selectedOrCreatedContact.user.company" :disabled="!selectedOrCreatedContact.isCreated" />
						</el-form-item>

						<el-form-item label="Vorname:" prop="firstName">
							<el-input v-model="selectedOrCreatedContact.user.firstName" :disabled="!selectedOrCreatedContact.isCreated" />
						</el-form-item>

						<el-form-item label="Nachname:" prop="lastName">
							<el-input v-model="selectedOrCreatedContact.user.lastName" :disabled="!selectedOrCreatedContact.isCreated" />
						</el-form-item>

						<el-form-item label="Gruppen:" prop="lastName">
							<el-select v-model="selectedRoles" multiple :disabled="!selectedOrCreatedContact.isCreated">
								<el-option v-for="option in statementGroupOptions" :key="option.value?.name" :label="option.label" :value="option.value?.name" />
							</el-select>
						</el-form-item>
					</el-col>
				</el-row>
			</TheBox>

			<TheBox :title="'Stellungnahme'">
				<el-form-item prop="answer">
					<el-input v-model="request.answer" :autosize="{ minRows: 10 }" type="textarea" placeholder="Bitte Text der Stellungnahme hier eingeben." />
				</el-form-item>
			</TheBox>
		</section>

		<DetailsMedia
			:files="request.files ? request.files : []"
			@addToFiles="addToFiles"
			@removeFromFiles="removeFromFiles"
			:pageType="'stellungnahme'"
		/>
		<DetailsLinks
			:links="request.links ? request.links : []"
			@addToLinks="addToLinks"
			@removeFromLinks="removeFromLinks"
			:pageType="'stellungnahme'"
		/>

		<section>
			<TheBox :title="'Interne Notiz (nur für Mitarbeiter sichtbar)'">
				<el-form-item prop="internalNote">
					<el-input v-model="request.internalNote" :autosize="{ minRows: 3 }" type="textarea" placeholder="Interne Notiz" />
				</el-form-item>
			</TheBox>
		</section>
	</TheForm>

	<DialogLeavePage v-model:open="openClosingDialog" @cancel="leavePage" @save="saveStatement(true)" />
</template>

<script lang="ts">
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import DialogLeavePage from '../components/dialogs/DialogLeavePage.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import InputLite from '../components/forms/InputLite.vue';

import DetailsLinks from './sections/DetailsLinks.vue';
import DetailsMedia from './sections/DetailsMedia.vue';
import { Delete, Plus, Loading } from '@element-plus/icons-vue';

import { defineComponent, ref, markRaw } from 'vue';
import { PostStatementRequest } from '@/types/formdataTypes';
import { ElMessage, FormInstance } from 'element-plus';
import type { RouteLocationRaw } from 'vue-router';
import { texts } from '../utils/texts';
import { validateForm, hasFormChanged, resetForm, getStartOfDayWithTime } from '@/utils/functions';

import { UserResponse, User, SearchContactRequest, FileDto, LinkDto, UserStatus, Gender, RoleResponse, RegisterRequest } from '@/types/types';
import saveIcon from '../assets/icons/saveIcon.vue';

import { postStatement } from '@/requests/statements';
import { returnFormOptions } from '../utils/formOptions.js';
import { searchContacts } from '@/requests/searches.js';
import { register } from '@/requests/auth';

interface ElSelectListItem {
	value: UserResponse;
	label: string;
}

interface CreatedOrSelectedContact {
	user: UserResponse;
	isCreated: boolean;
}

interface NewStatementData {
	request: PostStatementRequest;
	initialData: PostStatementRequest;
	selectedOrCreatedContact: CreatedOrSelectedContact;
	id: string;
	openClosingDialog: boolean;
	waiting: boolean;
	rules: Object;
	checkPageLeave: boolean;
	pageLeaveTo: RouteLocationRaw | null;
	contactsLoading: boolean;
	filteredContacts: ElSelectListItem[];
}

const initialRequestObj = {
	caseId: '',
	userId: '',
	title: '',
	files: [] as FileDto[],
	links: [] as LinkDto[],
	internalNote: '',
	answer: '',
	answeredDate: '',
	createdDate: '',
	contactName: '',
};
export default defineComponent({
	name: 'StellungnahmeNeu',
	data(): NewStatementData {
		return {
			request: { ...initialRequestObj, caseId: this.caseid },
			initialData: JSON.parse(JSON.stringify({ ...initialRequestObj, caseId: this.caseid })), // deep copy to ensure different objects
			selectedOrCreatedContact: {
				user: {
					id: '',
					hid: '',
					gender: null,
					firstName: '',
					userName: '',
					createdDate: '',
					lastName: '',
					email: '',
					postalCode: '',
					roles: [] as RoleResponse[],
					phoneNumber: '',
					status: UserStatus.Active,
					street: '',
					isContact: true,
				},
				isCreated: true,
			} as CreatedOrSelectedContact,
			id: 'statementForm',
			openClosingDialog: false,
			waiting: false,
			rules: {
				title: [{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] }],
				contactName: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'blur' }],
				createdDate: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
				answeredDate: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
				answer: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
				email: [{ type: 'email', message: texts.formErrors.emailMissing, trigger: 'blur' }],
			},
			checkPageLeave: true,
			pageLeaveTo: null,
			contactsLoading: false,
			filteredContacts: [],
		};
	},
	components: {
		ButtonBack,
		ButtonSticky,
		DialogLeavePage,
		TheBox,
		TheButton,
		TheForm,
		InputLite,
		Delete,
		Plus,
		saveIcon,
		DetailsLinks,
		DetailsMedia,
		Loading,
	},
	props: {
		caseid: {
			type: String,
			required: true,
		},
	},
	setup() {
		const formRef = ref<FormInstance>();

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		statementGroupOptions() {
			return returnFormOptions('contactRole');
		},
		genderOptions() {
			return returnFormOptions('gender');
		},
		initialUser() {
			return {
				id: '',
				hid: '',
				gender: Gender.Male,
				firstName: '',
				userName: '',
				createdDate: '',
				lastName: '',
				email: '',
				postalCode: '',
				roles: [] as RoleResponse[],
				phoneNumber: '',
				status: UserStatus.Active,
				street: '',
				isContact: true,
			} as UserResponse;
		},
		selectedRoles: {
			get(): string[] {
				return this.selectedOrCreatedContact.user.roles.map((r) => r.name);
			},
			set(value: string[]) {
				this.selectedOrCreatedContact.user.roles = value.map((s) => {
					return {
						id: '',
						name: s,
						normalizedName: '',
					};
				});
			},
		},
	},
	methods: {
		async saveStatement(leavePage = false) {
			this.openClosingDialog = false;

			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			let contactId = '';

			if (this.selectedOrCreatedContact.isCreated) {
				const createContact: RegisterRequest = {
					userName: this.selectedOrCreatedContact.user.email,
					initials: this.selectedOrCreatedContact.user.firstName.charAt(0) + this.selectedOrCreatedContact.user.lastName.charAt(0),
					email: this.selectedOrCreatedContact.user.email,
					firstName: this.selectedOrCreatedContact.user.firstName,
					seal: false,
					lastName: this.selectedOrCreatedContact.user.lastName,
					phoneNumber: this.selectedOrCreatedContact.user.phoneNumber || '',
					roles: this.selectedOrCreatedContact.user.roles.map((r) => {
						return {
							name: r.name,
						};
					}),
					company: this.selectedOrCreatedContact.user.company,
					titleBefore: this.selectedOrCreatedContact.user.titleBefore,
					titleAfter: this.selectedOrCreatedContact.user.titleAfter,
					gender: this.selectedOrCreatedContact.user.gender,
				};

				const registerResult = await register(createContact);
				if (registerResult.success) {
					contactId = registerResult.data.id;
				} else {
					ElMessage({ message: texts.statements.saveFailed, type: 'error', duration: 3000 });
					this.waiting = false;
					return;
				}
			} else {
				contactId = this.selectedOrCreatedContact.user.id;
			}

			const createRequest: PostStatementRequest = {} as PostStatementRequest;
			Object.entries(this.request).forEach(([key, val]) => {
				if (key === 'files' || key === 'links') return;
				if (typeof val === 'object' && val && 'value' in val) {
					createRequest[key] = val.value;
				} else {
					createRequest[key] = val;
				}
			});

			if (this.request.links && this.request.links.length > 0) {
				createRequest.links = this.request.links?.map((link) => {
					return {
						name: link.link.name,
						url: link.link.url,
						type: link.link.type,
					} as LinkDto;
				});
			}

			if (this.request.files && this.request.files.length > 0) {
				createRequest.files = this.request.files?.map((file) => {
					return {
						fileName: file.file.fileName,
						bytes: file.file.bytes,
						contentType: file.file.contentType,
					} as FileDto;
				});
			}

			createRequest.answeredDate = getStartOfDayWithTime(createRequest.answeredDate, 10);
			createRequest.createdDate = getStartOfDayWithTime(createRequest.createdDate, 10);

			createRequest.userId = contactId;

			const result = await postStatement(createRequest);
			if (result.success) {
				if (leavePage) this.leavePage();
				else {
					this.checkPageLeave = false;
					this.$router.push({ name: 'StellungnahmeDetail', params: { id: result.data.id } });
				}
			} else {
				ElMessage({ message: texts.statements.createFailed, type: 'error', duration: 3000 });
			}

			this.waiting = false;
			waitingMsg.close();
		},
		async doContactSearch(val) {
			if (!val) return;

			this.filteredContacts = [];
			this.contactsLoading = true;
			this.request.contactName = val;

			const requestSearchContact = {
				searchTerm: this.request.contactName,
				withUsers: true,
			} as SearchContactRequest;

			const searchResult = await searchContacts(requestSearchContact, 0);
			if (searchResult.success) {
				// @ts-ignore
				this.filteredContacts = searchResult.data.items.map((item) => {
					return {
						value: item,
						label: String(item.firstName) + ' ' + String(item.lastName),
					};
				});
			}

			this.contactsLoading = false;
		},
		clearSelection() {
			this.selectedOrCreatedContact = {
				user: this.initialUser,
				isCreated: true,
			};
		},
		focusRemoteSearchSelect() {
			this.request.contactName = '';
		},
		setContact(contact: UserResponse) {
			if (contact) {
				this.request.contactName = contact.firstName + ' ' + contact.lastName;
				this.selectedOrCreatedContact = {
					user: contact,
					isCreated: false,
				};
			}
		},
		addToFiles(file) {
			if (!this.request.files) this.request.files = [];
			this.request.files.push(file);
		},
		removeFromFiles(file) {
			this.request.files = this.request.files?.filter((f) => f.file !== file.file);
		},
		removeFromLinks(link) {
			this.request.links = this.request.links?.filter((l) => l.link !== link.link);
		},
		addToLinks(link) {
			if (!this.request.links) this.request.links = [];
			this.request.links.push(link);
		},
		leavePage() {
			this.openClosingDialog = false;
			this.checkPageLeave = false;
			// @ts-ignore
			this.$router.push(this.pageLeaveTo);
		},
		handleBeforeRouteLeave(to, from, next) {
			if (this.checkPageLeave === false) {
				resetForm(this.formRef, this.id);
				next();
				return;
			}

			// check if request data has changed
			const hasChanged = hasFormChanged({ initialData: this.initialData, currentData: this.request });

			if (!hasChanged) {
				next();
				return;
			}

			// ABORT NAVIGATION & SHOW DIALOG
			this.openClosingDialog = true;
			this.pageLeaveTo = to;
			next(false);
		},
	},
	mounted() {
		resetForm(this.formRef, this.id);
	},
	beforeRouteLeave(to, from, next) {
		this.handleBeforeRouteLeave(to, from, next);
	},
});
</script>
